<template>
    <model-form
        :title="
            taskReference && taskReference.id
                ? `Edit ${taskReference.name}`
                : 'Create Task Reference'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    />
</template>
<script>
import { defineComponent } from "vue";

import ModelForm from "../common/models/ModelForm.vue";
import { buildNameField, buildSelectField, buildTextField } from "@/lib/vueModelUtils";
import {
    buildThingSchemaSelectField,
    getAllAttributeOptionsForSchema,
    getDateAttributeOptions,
    getThingSchemaForId,
    schemaContainsDateAttributes,
} from "@/lib/thingUtils";
import { buildTaskReferencesAPIForExperiment } from "@/services/api.services";
import { numberArrayToText, parseToNumberArray } from "@/lib/arrayUtils";

export default defineComponent({
    props: {
        taskReference: {
            type: Object,
            required: false,
        },
        protocol: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    components: {
        ModelForm,
    },

    data() {
        return {
            selectedThingSchema: null,
            nameField: null,
            daysAfterReferenceField: null,
            referenceExperimentStartDateField: null,
            thingSchemaField: null,
            referenceAttributeNameField: null,
            formFields: [],
        };
    },

    mounted() {
        this.taskReferencesAPI = buildTaskReferencesAPIForExperiment(this.experiment.code);
        this.buildFormFields();
    },

    methods: {
        buildFormFields: function () {
            this.nameField = buildNameField(this.taskReference.name);

            this.daysAfterReferenceField = buildTextField({
                label: "Days After Reference",
                value: numberArrayToText(this.taskReference.daysAfterReference),
                required: true,
            });

            this.referenceExperimentStartDateField = {
                fieldType: "boolean",
                fieldDisplayName: "Reference Experiment Start Date?",
                value: this.taskReference.referenceExperimentStartDate,
                changed: () => {
                    this.updateFormFields();
                },
            };

            this.selectedThingSchema = getThingSchemaForId(
                this.protocol,
                this.taskReference.thingSchemaId
            );
            this.thingSchemaField = buildThingSchemaSelectField({
                label: "Schema",
                abstractSchemaConfig: this.protocol,
                initialThingSchema: this.selectedThingSchema,
                changed: (schema) => {
                    this.selectedThingSchema = schema;
                    this.updateAttributeOptions();
                },
                schemaOptionFilterFn: (schema) => {
                    return schemaContainsDateAttributes(schema);
                },
            });

            this.referenceAttributeNameField = buildSelectField({
                name: "Reference Attribute",
                options: getAllAttributeOptionsForSchema(this.selectedThingSchema),
                value: this.taskReference.referenceAttributeName,
            });

            this.updateFormFields();
        },
        updateFormFields() {
            const formFields = [
                this.nameField,
                this.daysAfterReferenceField,
                this.referenceExperimentStartDateField,
            ];
            if (!this.referenceExperimentStartDateField.value) {
                formFields.push(this.thingSchemaField);
                formFields.push(this.referenceAttributeNameField);
            }

            this.formFields = formFields;
        },
        updateAttributeOptions() {
            this.referenceAttributeNameField.options = getDateAttributeOptions(
                this.selectedThingSchema
            );
            if (this.referenceAttributeNameField.options.length === 1) {
                this.referenceAttributeNameField.value =
                    this.referenceAttributeNameField.options[0].value;
            }
        },
        async save() {
            const referenceExperimentStartDate = this.referenceExperimentStartDateField.value;
            const taskReference = {
                ...this.taskReference,
                name: this.nameField.value,
                daysAfterReference: parseToNumberArray(this.daysAfterReferenceField.value),
                referenceExperimentStartDate: this.referenceExperimentStartDateField.value,
                thingSchemaId: referenceExperimentStartDate ? null : this.selectedThingSchema.id,
                referenceAttributeName: referenceExperimentStartDate
                    ? null
                    : this.referenceAttributeNameField.value,
            };

            if (this.taskReference.id) {
                await this.taskReferencesAPI.update(taskReference);
            } else {
                await this.taskReferencesAPI.create(taskReference);
            }
            this.$emit("saved");
        },
        cancel() {
            this.$emit("closed");
        },
        close() {
            this.$emit("closed");
        },
    },
});
</script>
